import React from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect } from 'react';
import {
  Login,
  MfaVerify,
  Sicherheit,
  Error,
  HomeLayout,
  Register,
  DashboardLayout,
  SettingsLayout,
  Kartenverwaltung,
  Links,
  DashboardIntro,
  Erscheinungsbild,
  CardPage,
  VerifyEmail,
  ForgotPassword,
  ResetPassword,
  EmailChange,
  Produkte,
  Checkout,
  Linkvalidation,
  Downloads,
  AcceptInvitation,
} from './pages';

import {
  OrderPage,
  PaymentSuccess,
  ProductDetail,
  ProductGrid,
  ShoppingCart,
  ErrorElement,
  UserList,
  UserDetail,
  OrderList,
  CardList,
  CardDetail,
  CompanyList,
  CompanyDetail,
  CompanyUsers,
  CompanyBranding,
  CompanySubscription,
  CompanySettings,
  CompanyUserDetail,
  CompanyStatistics,
} from './components';

import { action as registerAction } from './pages/Register';
import { action as loginAction } from './pages/Login';
import { action as linkEditAction } from './components/dashboard/links/verwaltung/LinkEditSlideIn';
import { action as personalSettingsAction } from './components/dashboard/Settings';
import { action as linkSettingsAction } from './components/dashboard/links/erscheinungsbild/ErscheinungsbildForm';
import { action as forgotPasswordAction } from './pages/ForgotPassword';
import { action as resetPasswordAction } from './pages/ResetPassword';
import { action as orderAction } from './components/bestellungen/OrderPage';
import { action as benutzerDetailAction } from './components/dashboard/admin/benutzerverwaltung/userDetail';
import { loader as mfaVerifyLoader } from './pages/MfaVerify';
import { loader as securityLoader } from './components/dashboard/Security';
import { loader as DashboardLoader } from './pages/DashboardLayout';
import { loader as LinksLoader } from './pages/Links';
import { loader as publicLinksLoader } from './pages/CardPage';
import { loader as linkSettingsLoader } from './pages/Erscheinungsbild';
import { loader as verifyEmailLoader } from './pages/VerifyEmail';
import { loader as resetPasswordLoader } from './pages/ResetPassword';
import { loader as productsLoader } from './components/produkte/ProductGrid';
import { loader as singleProductLoader } from './components/produkte/ProductDetail';
import { loader as bestellungenLoader } from './components/bestellungen/OrderPage';
import { loader as settingsLoader } from './components/dashboard/Settings';
import { loader as cardsLoader } from './components/dashboard/kartenverwaltung/CardList';
import { loader as benutzerverwaltungsLoader } from './components/dashboard/admin/benutzerverwaltung/userList';
import { loader as benutzerDetailLoader } from './components/dashboard/admin/benutzerverwaltung/userDetail';
import { loader as orderListLoader } from './components/dashboard/admin/bestellbearbeitung/orderList';
import { loader as cardListLoader } from './components/dashboard/admin/kartenadministration/cardList';
import { loader as cardDetailLoader } from './components/dashboard/admin/kartenadministration/CardDetail';
import { loader as companyListLoader } from './components/dashboard/admin/firmenverwaltung/CompanyList';
import { loader as companyDetailLoader } from './components/dashboard/admin/firmenverwaltung/CompanyDetail';
import { loader as companyUsersLoader } from './components/dashboard/company/CompanyUsers';
import { loader as companyBrandingLoader } from './components/dashboard/company/CompanyBranding';
import { loader as companySubscriptionLoader } from './components/dashboard/company/CompanySubscription';
import { loader as companySettingsLoader } from './components/dashboard/company/CompanySettings';
import { loader as companyUserDetailLoader } from './components/dashboard/company/CompanyUserDetail';
import { action as companyUserDetailAction } from './components/dashboard/company/CompanyUserDetail';
import { loader as companyStatisticsLoader } from './components/dashboard/company/CompanyStatistics';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
    },
  },
});

const getRedirectComponent = (path) => {
  // In Entwicklungsumgebung zeige nur Hinweis an, in Produktion leite weiter
  if (import.meta.env.DEV) {
    return () => (
      <div className="p-4 bg-yellow-100 border-l-4 border-yellow-500">
        <p>
          In production, this would redirect to: https://www.svisit.ch{path}
        </p>
        <button
          className="mt-2 bg-blue-500 text-white px-3 py-1 rounded-sm"
          onClick={() => window.open(`https://www.svisit.ch${path}`, '_blank')}
        >
          Visit URL
        </button>
      </div>
    );
  }

  return () => {
    useEffect(() => {
      window.location.href = `https://www.svisit.ch${path}`;
    }, []);
    return <p>Redirecting...</p>;
  };
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <HomeLayout />,
    errorElement: <Error />,
    children: [
      {
        index: true,
        element: React.createElement(getRedirectComponent('')),
      },
      {
        path: 'register',
        element: <Register />,
        action: registerAction,
      },
      {
        path: 'login',
        element: <Login />,
        action: loginAction(queryClient),
      },
      {
        path: 'login/verify',
        element: <MfaVerify />,
        loader: mfaVerifyLoader,
      },
      {
        path: '/ueber-uns',
        element: React.createElement(getRedirectComponent('/ueber-uns')),
      },
      {
        path: 'verify-email',
        element: <VerifyEmail />,
        loader: verifyEmailLoader,
      },
      {
        path: 'invitation/accept',
        element: <AcceptInvitation />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPassword />,
        action: forgotPasswordAction,
      },
      {
        path: 'reset-password',
        element: <ResetPassword />,
        action: resetPasswordAction,
        loader: resetPasswordLoader,
      },
      {
        path: 'email-change',
        element: <EmailChange />,
      },
      {
        path: 'impressum',
        element: React.createElement(getRedirectComponent('/impressum')),
      },
      {
        path: 'datenschutz',
        element: React.createElement(getRedirectComponent('/datenschutz')),
      },
      {
        path: 'agb',
        element: React.createElement(getRedirectComponent('/agb')),
      },
    ],
  },
  {
    path: '/dashboard',
    element: <DashboardLayout queryClient={queryClient} />,
    loader: DashboardLoader(queryClient),
    children: [
      {
        index: true,
        element: <DashboardIntro />,
      },
      {
        path: 'links',
        errorElement: <ErrorElement />,
        children: [
          {
            index: true,
            element: <Navigate to="verwalten" replace />,
          },
          {
            path: 'verwalten',
            element: <Links />,
            loader: LinksLoader(queryClient),
            action: linkEditAction(queryClient),
          },
          {
            path: 'erscheinungsbild',
            element: <Erscheinungsbild />,
            loader: linkSettingsLoader(queryClient),
            action: linkSettingsAction(queryClient),
          },
          {
            path: 'downloads',
            element: <Downloads />,
          },
        ],
      },
      {
        path: 'statistiken',
        async lazy() {
          const { default: Statistiken, loader } = await import('./pages/Statistiken');
          return { Component: Statistiken, loader: loader(queryClient) };
        },
        errorElement: <ErrorElement />,
      },
      {
        path: 'kartenverwaltung',
        errorElement: <ErrorElement />,
        children: [
          {
            index: true,
            element: <Navigate to="karten" replace />,
          },
          {
            path: 'karten',
            element: <Kartenverwaltung />,
            loader: cardsLoader(queryClient),
          },
          {
            path: 'entwuerfe',
            async lazy() {
              const { default: Entwuerfe, loader } = await import('./pages/Entwuerfe');
              return { Component: Entwuerfe, loader: loader(queryClient) };
            },
          },
        ],
      },
      {
        path: 'bestellungen',
        async lazy() {
          const { default: Bestellungen, loader } = await import('./pages/Bestellungen');
          return { Component: Bestellungen, loader: loader(queryClient) };
        },
        errorElement: <ErrorElement />,
      },
      {
        path: 'einstellungen',
        element: <SettingsLayout />,
        action: personalSettingsAction(queryClient),
        loader: settingsLoader(queryClient),
        errorElement: <ErrorElement />,
      },
      {
        path: 'sicherheit',
        element: <Sicherheit />,
        loader: securityLoader(queryClient),
        errorElement: <ErrorElement />,
      },
      {
        path: 'benutzerverwaltung',
        errorElement: <ErrorElement />,
        children: [
          {
            index: true,
            element: <UserList />,
            loader: benutzerverwaltungsLoader(queryClient),
          },
          {
            path: ':id',
            loader: benutzerDetailLoader,
            action: benutzerDetailAction,
            element: <UserDetail />,
          },
        ],
      },
      {
        path: 'bestellbearbeitung',
        errorElement: <ErrorElement />,
        children: [
          {
            index: true,
            element: <OrderList />,
            loader: orderListLoader(queryClient),
          },
          {
            path: 'neu',
            async lazy() {
              const { default: NewManualOrder } = await import('./components/dashboard/admin/bestellbearbeitung/NewManualOrder');
              return { Component: NewManualOrder };
            },
          },
          {
            path: ':id',
            async lazy() {
              const { default: OrderDetail, loader } = await import('./components/dashboard/admin/bestellbearbeitung/OrderDetail');
              return { Component: OrderDetail, loader };
            },
          },
        ],
      },
      {
        path: 'kartenadministration',
        errorElement: <ErrorElement />,
        children: [
          {
            index: true,
            element: <CardList />,
            loader: cardListLoader(queryClient),
          },
          {
            path: ':id',
            loader: cardDetailLoader,
            element: <CardDetail />,
          },
        ],
      },
      {
        path: 'firmenverwaltung',
        errorElement: <ErrorElement />,
        children: [
          {
            index: true,
            element: <CompanyList />,
            loader: companyListLoader(queryClient),
          },
          {
            path: ':id',
            loader: companyDetailLoader(queryClient),
            element: <CompanyDetail />,
          },
          {
            // Der Plattform-Admin bestellt für diese Firma — dasselbe
            // Formular wie die Firmenadmins, im Admin-Modus.
            path: ':id/bestellung',
            async lazy() {
              const { default: NewCompanyOrder } = await import(
                './components/dashboard/company/orders/NewCompanyOrder'
              );
              return { Component: NewCompanyOrder };
            },
          },
        ],
      },
      {
        path: 'protokoll',
        async lazy() {
          const { default: AuditLogList, loader } = await import(
            './components/dashboard/admin/protokoll/AuditLogList'
          );
          return { Component: AuditLogList, loader: loader(queryClient) };
        },
        errorElement: <ErrorElement />,
      },
      {
        path: 'firma',
        errorElement: <ErrorElement />,
        children: [
          {
            path: 'mitarbeiter',
            element: <CompanyUsers />,
            loader: companyUsersLoader(queryClient),
          },
          {
            path: 'mitarbeiter/:userId',
            element: <CompanyUserDetail />,
            loader: companyUserDetailLoader(queryClient),
            action: companyUserDetailAction(queryClient),
          },
          {
            path: 'statistiken',
            element: <CompanyStatistics />,
            loader: companyStatisticsLoader(queryClient),
          },
          {
            path: 'branding',
            element: <CompanyBranding />,
            loader: companyBrandingLoader(queryClient),
          },
          {
            path: 'abonnement',
            element: <CompanySubscription />,
            loader: companySubscriptionLoader(queryClient),
          },
          {
            path: 'einstellungen',
            element: <CompanySettings />,
            loader: companySettingsLoader(queryClient),
          },
          {
            path: 'kartendesigns',
            async lazy() {
              const { default: CompanyDesigns, loader } = await import(
                './components/dashboard/company/designs/CompanyDesigns'
              );
              return { Component: CompanyDesigns, loader: loader(queryClient) };
            },
          },
          {
            path: 'bestellungen',
            async lazy() {
              const { default: CompanyOrders, loader } = await import(
                './components/dashboard/company/orders/CompanyOrders'
              );
              return { Component: CompanyOrders, loader: loader(queryClient) };
            },
          },
          {
            path: 'bestellungen/neu',
            async lazy() {
              const { default: NewCompanyOrder } = await import(
                './components/dashboard/company/orders/NewCompanyOrder'
              );
              return { Component: NewCompanyOrder };
            },
          },
          {
            path: 'karten',
            async lazy() {
              const { default: CompanyCards } = await import(
                './components/dashboard/company/cards/CompanyCards'
              );
              return { Component: CompanyCards };
            },
          },
          {
            path: 'bestellungen/:orderId',
            async lazy() {
              const { default: CompanyOrderDetail } = await import(
                './components/dashboard/company/orders/CompanyOrderDetail'
              );
              return { Component: CompanyOrderDetail };
            },
          },
        ],
      },
    ],
  },
  {
    path: '/c/:id',
    element: <CardPage />,
    loader: publicLinksLoader,
  },
  {
    path: '/l/:id',
    element: <Linkvalidation />,
  },
  {
    path: '/produkte',
    element: <Produkte />,
    children: [
      {
        index: true,
        element: <ProductGrid />,
        loader: productsLoader,
      },
      {
        path: ':id',
        element: <ProductDetail />,
        loader: singleProductLoader,
      },
    ],
  },
  {
    path: '/karten-designer/:productId?',
    async lazy() {
      const { default: DesignerPage } = await import(
        './features/cardDesigner/DesignerPage'
      );
      return { Component: DesignerPage };
    },
    errorElement: <Error />,
  },
  {
    path: '/warenkorb',
    element: <Checkout />,
    children: [
      {
        index: true,
        element: <ShoppingCart />,
      },
      {
        path: 'bestellung',
        element: <OrderPage />,
        loader: bestellungenLoader,
        action: orderAction,
      },
      {
        path: 'bezahlung',
        element: <PaymentSuccess />,
      },
    ],
  },
]);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
